import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  CanonicalAnalysisOutput,
  ScorecardAssetOption,
  ScorecardPayload,
  ScorecardRowRef,
} from '@digital-twins/shared-types'

import { KpiPlot, kpiScorecardAdapter } from '@digital-twins/deck-ui/plots/kpi'

import { formatAbsoluteScore } from './formatAbsoluteScore'
import { MetricFigure, MetricFigureSource, resolveMetricFigure } from './resolveMetricFigure'

interface SummaryScorecardProps {
  payload: ScorecardPayload
  analysis?: CanonicalAnalysisOutput | null
  survey?: unknown
  itemTags: Record<string, string>
}

interface ScorecardCell {
  columnId: string
  figure: MetricFigure | null
}

interface ScorecardResolvedRow {
  /** Unique per rendered row: the metric, plus the grid row when cut per row. */
  key: string
  metricId: string
  label: string
  questionText?: string | null
  higherIsBetter?: boolean
  baseSize?: number
  cells: Array<ScorecardCell>
}

/**
 * Shared highlight for the item-letter badge and the one figure a row calls
 * out. The letter trains the eye; the winning score reuses it, so "this one
 * wins" is not a second visual language.
 *
 * The pair is `--report-sentiment-pos-bg` / `--report-sentiment-pos-text`,
 * the same chip the report already uses for a positive mark. Those two tokens
 * flip together: pale mint with dark teal ink in light mode, dark teal with
 * mint ink in dark mode. `--report-accent-light` / `--report-accent-mid` do
 * not — in light mode the "mid" token is the pale mint, so using it as text
 * on a light card washes the letter and the winning score out.
 */
const ACCENT_CHIP_CLASS =
  'bg-[hsl(var(--report-sentiment-pos-bg))] text-[hsl(var(--report-sentiment-pos-text))]'

const WINNER_CHIP_CLASS = `rounded-full px-2.5 py-0.5 justify-self-center ${ACCENT_CHIP_CLASS}`

/**
 * The measure-name column stays in view while the figures scroll under it.
 * The slot frame has no fill, so the page background is what hides the
 * figures passing beneath the label.
 */
const STICKY_LABEL_CLASS = 'sticky left-0 z-10 bg-[hsl(var(--report-bg))]'

function LowerIsBetterMark(props: { label: string }) {
  const { label } = props
  const { t } = useTranslation('app')

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger
          type="button"
          data-testid="summary-lower-is-better"
          aria-label={t('studyReport.scorecardLowerIsBetter', { label })}
          className="ml-1 text-[10px] font-semibold text-muted-foreground cursor-default align-middle"
        >
          ↓
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] text-center">
          {t('studyReport.scorecardLowerIsBetterHint')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function polarityRank(figure: MetricFigure | null): number {
  if (!figure || figure.nativeValue == null) {
    return Number.NEGATIVE_INFINITY
  }
  if (figure.higherIsBetter === false) {
    return -figure.nativeValue
  }
  return figure.nativeValue
}

function figureForSort(row: ScorecardResolvedRow): MetricFigure | null {
  const withValue = row.cells.find((cell) => cell.figure?.nativeValue != null)
  return withValue?.figure ?? row.cells.find((cell) => cell.figure)?.figure ?? null
}

/**
 * The column whose figure wins the row, or `null` when there is nothing to
 * crown: fewer than two scored cells, or an EXACT TIE at the top. The old
 * implementation seeded `best` with the first scored cell and only replaced
 * it on a strictly-greater rank, so two cells tied at the top silently
 * crowned whichever happened to sort first -- a fabricated winner. Tracking
 * how many cells sit at the best rank fixes that without changing the
 * (already correct) strict `>` comparison.
 */
function betterCellId(cells: Array<ScorecardCell>): string | null {
  const scored = cells.filter((cell) => cell.figure?.nativeValue != null)
  if (scored.length < 2) {
    return null
  }
  let bestRank = Number.NEGATIVE_INFINITY
  let bestId: string | null = null
  let tiedAtBest = 0
  for (const cell of scored) {
    const rank = polarityRank(cell.figure)
    if (rank > bestRank) {
      bestRank = rank
      bestId = cell.columnId
      tiedAtBest = 1
    } else if (rank === bestRank) {
      tiedAtBest += 1
    }
  }
  return tiedAtBest === 1 ? bestId : null
}

function resolveScorecardRows(
  refs: Array<ScorecardRowRef>,
  analysis: CanonicalAnalysisOutput | null | undefined,
  survey: unknown,
  columns: Array<ScorecardAssetOption>,
  isHeadToHead: boolean,
): Array<ScorecardResolvedRow> {
  const source: MetricFigureSource = analysis ? { ...analysis, survey } : { survey }
  const rows: Array<ScorecardResolvedRow> = []

  for (const ref of refs) {
    // D2: a grid measure over items is one row per grid row, read off
    // `breakdown[rowId]` with that row's own base, never the pooled mean.
    const rowId = ref.rowId ?? null
    const overall = rowId
      ? resolveMetricFigure(source, ref.metricId, rowId)
      : resolveMetricFigure(source, ref.metricId)
    const cells: Array<ScorecardCell> = isHeadToHead && !rowId
      ? columns.map((column) => ({
          columnId: column.id,
          figure: resolveMetricFigure(source, ref.metricId, column.id),
        }))
      : [{ columnId: 'score', figure: overall }]

    if (!cells.some((cell) => cell.figure?.nativeValue != null)) {
      continue
    }

    const labeled = overall ?? cells.find((cell) => cell.figure)?.figure ?? null
    rows.push({
      key: rowId ? `${ref.metricId}:${rowId}` : ref.metricId,
      metricId: ref.metricId,
      label: ref.rowLabel?.trim() || (labeled?.label ?? ref.metricId),
      questionText: labeled?.questionText ?? null,
      higherIsBetter: labeled?.higherIsBetter,
      baseSize: labeled?.baseSize,
      cells,
    })
  }

  return rows.sort((left, right) => polarityRank(figureForSort(right)) - polarityRank(figureForSort(left)))
}

/**
 * Smallest resolved base across every row this exhibit renders (both blocks,
 * in the mixed-scope case), mirroring `KpiListView`'s `exhibitBaseSize`: a
 * single "n=" that overstates any one row's read is the dishonest number.
 */
function scorecardBaseSize(rows: Array<ScorecardResolvedRow>): number | null {
  const bases: Array<number> = []
  for (const row of rows) {
    if (row.baseSize != null && row.baseSize > 0) {
      bases.push(row.baseSize)
    }
  }
  if (bases.length === 0) {
    return null
  }
  return Math.min(...bases)
}

interface ScorecardBlockProps {
  columns: Array<ScorecardAssetOption>
  rows: Array<ScorecardResolvedRow>
  itemTags: Record<string, string>
  isHeadToHead: boolean
}

function ScorecardBlock(props: ScorecardBlockProps) {
  const { columns, rows, itemTags, isHeadToHead } = props
  // PER-ITEM: the measure name sits on its own line above its figures, and
  // the item columns take the card's whole width. A name column beside three
  // item columns cannot fit the Insights column (about 185-225px of content at
  // 1280-1440px): the saved 349 report clipped its third ad even at 64px + 3 x
  // 56px. Stacked, three items need 3 x 56px + 2 gaps = 180px.
  //
  // POOLED keeps the name beside its single "Score" column, which always fits.
  //
  // The floor, 56px, is a value pill ("4.2/5" in the winner chip). It is fixed
  // rather than `min-content` because the header and every row are separate
  // grids, and only one template shared by all of them keeps the columns
  // aligned. When even the floors do not fit (five or six items), the block
  // scrolls sideways with the scrollbar always drawn.
  const valueColumns = columns.map(() => 'minmax(56px,1fr)').join(' ')
  const gridTemplateColumns = isHeadToHead ? valueColumns : `minmax(64px, 2.6fr) ${valueColumns}`
  const gapClass = isHeadToHead ? 'gap-x-1.5 gap-y-1' : 'gap-2'

  return (
    <div
      data-testid="summary-scorecard-scroll"
      className="flex flex-col overflow-x-auto pb-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30"
    >
      {/* The one-column header (D4): a lone unlabeled number column reads as
          nothing in particular, so even the plain pooled scorecard now names
          it "Score" the same way a per-item grid names its columns. */}
      <div className={`grid ${gapClass} min-w-fit`} style={{ gridTemplateColumns }}>
        {!isHeadToHead && <span className={STICKY_LABEL_CLASS} />}
        {columns.map((column) => {
          const tag = itemTags[column.id]
          return (
            <span
              key={column.id}
              data-testid={`summary-scorecard-column-${column.id}`}
              className="flex flex-col items-center gap-1"
            >
              {tag && (
                <span
                  aria-hidden="true"
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold leading-none ${ACCENT_CHIP_CLASS}`}
                >
                  {tag}
                </span>
              )}
              <span
                title={column.label}
                className="text-[11px] font-medium text-muted-foreground text-center leading-tight break-words"
              >
                {column.label}
              </span>
            </span>
          )
        })}
      </div>

      {/* Head-to-head stacks the question under the version labels. Without
          this gap the first question sits flush against "Version A" / "Version B".
          The pooled block keeps its header on the same grid as the score. */}
      <div
        data-testid="summary-scorecard-rows"
        className={`flex flex-col min-w-fit ${isHeadToHead ? 'mt-4' : ''}`}
      >
        {rows.map((row, index) => {
          const winnerId = isHeadToHead ? betterCellId(row.cells) : null

          return (
            <div key={row.key} data-testid={`summary-scorecard-row-${row.key}`}>
              {index > 0 && <div className="border-t border-border/50 my-2" />}
              <div className={`grid items-center ${gapClass}`} style={{ gridTemplateColumns }}>
                <Text
                  size="sm"
                  title={row.questionText || row.label}
                  data-testid={`summary-scorecard-label-${row.metricId}`}
                  className={`text-xs text-muted-foreground leading-snug break-words ${STICKY_LABEL_CLASS}`}
                  style={isHeadToHead ? { gridColumn: '1 / -1' } : undefined}
                >
                  {row.label}
                  {row.higherIsBetter === false && <LowerIsBetterMark label={row.label} />}
                </Text>
                {row.cells.map((cell) => {
                  const value = cell.figure?.nativeValue
                  const unit = cell.figure?.unit ?? ''
                  const cellTestId = isHeadToHead
                    ? `summary-scorecard-cell-${row.key}-${cell.columnId}`
                    : `summary-scorecard-cell-${row.key}-score`
                  const isWinner = winnerId === cell.columnId

                  if (value == null) {
                    return (
                      <span
                        key={cell.columnId}
                        data-testid={cellTestId}
                        className="text-sm font-medium tabular-nums text-foreground text-center"
                      >
                        —
                      </span>
                    )
                  }

                  return (
                    <span
                      key={cell.columnId}
                      data-testid={cellTestId}
                      data-polarity-better={isWinner ? 'true' : undefined}
                      className={`text-sm font-medium tabular-nums text-center ${
                        isWinner ? WINNER_CHIP_CLASS : 'text-foreground'
                      }`}
                    >
                      {formatAbsoluteScore(value, unit)}
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function SummaryScorecard(props: SummaryScorecardProps) {
  const { payload, analysis, survey, itemTags } = props
  const { t } = useTranslation('app')
  const scoreColumn: ScorecardAssetOption = {
    id: 'score',
    label: t('studyReport.scorecardScoreColumn'),
  }

  // Plots plan F: a scorecard Python stamped carries its figures, bases, order
  // and each row's winner as a `kpi` plot; nothing below is read or decided
  // here. Everything after this branch is the legacy reader for scorecards
  // stored before the stamp (`resolveMetricFigure`, browser sort and crown).
  if (kpiScorecardAdapter.accepts(payload)) {
    const stored = kpiScorecardAdapter.read(payload)
    return (
      <KpiPlot
        data={stored.data}
        props={{ ...stored.props, layout: 'grid', showBase: 'header', highlight: 'top' }}
        labelOverrides={{
          plot_base_n: t('studyReport.scorecardBase', { n: '{n}' }),
          too_close_to_call: t('studyReport.tooCloseToCall'),
          plot_not_available: t('studyReport.plotNotAvailable'),
          plot_nps_score: t('studyReport.plotNpsScore'),
          stat_tab_average: t('studyReport.statTabAverage'),
          stat_tab_top_n: t('studyReport.statTabTopN', { n: '{n}' }),
          stat_tab_nps: t('studyReport.statTabNps'),
        }}
        testIdPrefix="summary-scorecard"
        itemTags={itemTags}
        scoreColumnLabel={scoreColumn.label}
        overallHeading={t('studyReport.scorecardOverallMeasures')}
        renderLowerIsBetter={(label) => <LowerIsBetterMark label={label} />}
      />
    )
  }
  const assetOptions = payload.assetOptions ?? []
  const wantsHeadToHead = assetOptions.length >= 2

  const perItemRows = wantsHeadToHead
    ? resolveScorecardRows(payload.rows, analysis, survey, assetOptions, true)
    : []
  const perItemMetricIds = new Set(perItemRows.map((row) => row.metricId))
  /**
   * `_build_scorecard` turns per-item columns on when ANY row breaks down by
   * item, so a study can carry a per-ad appeal grid beside a pooled purchase-
   * intent read on the SAME card. Every row the per-item pass above could not
   * place -- either because the whole card is pooled (`wantsHeadToHead` is
   * false) or because this one measure was fielded pooled while its
   * neighbours went per-item -- is a candidate for the pooled block.
   */
  const pooledCandidates = wantsHeadToHead
    ? payload.rows.filter((ref) => !perItemMetricIds.has(ref.metricId))
    : payload.rows
  const pooledRows =
    pooledCandidates.length > 0
      ? resolveScorecardRows(pooledCandidates, analysis, survey, [scoreColumn], false)
      : []

  if (perItemRows.length === 0 && pooledRows.length === 0) {
    return null
  }

  const baseSize = scorecardBaseSize([...perItemRows, ...pooledRows])
  const isMixedScope = perItemRows.length > 0 && pooledRows.length > 0

  return (
    <div className="flex flex-col gap-3" data-testid="summary-scorecard">
      {baseSize != null && (
        <Text
          size="sm"
          data-testid="summary-scorecard-base"
          className="text-[10px] text-muted-foreground tabular-nums self-end"
        >
          {t('studyReport.scorecardBase', { n: baseSize })}
        </Text>
      )}

      {perItemRows.length > 0 && (
        <ScorecardBlock
          columns={assetOptions}
          rows={perItemRows}
          itemTags={itemTags}
          isHeadToHead
        />
      )}

      {/* Mixed scope only: a neutral heading, never an item noun or an "all
          ads" claim -- item tags are position letters (A, B, C), and a
          measure with no per-item breakdown does not prove the read covered
          every item. */}
      {isMixedScope && (
        <Text
          size="sm"
          data-testid="summary-scorecard-overall-heading"
          className="text-xs font-semibold text-muted-foreground"
        >
          {t('studyReport.scorecardOverallMeasures')}
        </Text>
      )}

      {pooledRows.length > 0 && (
        <ScorecardBlock
          columns={[scoreColumn]}
          rows={pooledRows}
          itemTags={itemTags}
          isHeadToHead={false}
        />
      )}
    </div>
  )
}
